/**
 * URL Sharing Utilities
 *
 * Functions for encoding/decoding ZFS Calculator configuration in URL parameters.
 * This allows users to share their configurations via links.
 */

import type {
  StorageTopology,
  DriveParameters,
  ZFSParameters,
  CapacityUtilization,
  EconomicsParameters,
  PerformanceInputs,
} from '../types';

/**
 * Minimal configuration representation for URL encoding
 * Only includes non-default values to minimize URL length
 */
interface MinimalConfig {
  t?: {
    r?: StorageTopology['raidLevel'];
    d?: number; // drivesPerVdev
    v?: number; // vdevCount
  };
  dr?: {
    s?: number; // driveSizeTB
    c?: number; // driveCost
    t?: DriveParameters['driveType'];
  };
  z?: {
    a?: number; // ashift
    cr?: number; // compressionRatio
  };
  u?: {
    f?: number; // poolFillPercent
    l?: number; // freeSpaceLimitPercent
    sd?: number; // scrubIntervalDays
  };
  e?: {
    r?: number; // electricityRate
    h?: number; // uptimeHoursPerDay
  };
  p?: {
    w?: PerformanceInputs['workloadType'];
    rp?: number; // readPercentage
  };
}

/**
 * Compress configuration into URL-safe base64 string
 *
 * @param config - Full configuration state
 * @returns Complete URL with encoded configuration parameter
 */
export function configToURL(config: {
  topology: StorageTopology;
  drive: DriveParameters;
  zfs: ZFSParameters;
  utilization: CapacityUtilization;
  economics: EconomicsParameters;
  performance: PerformanceInputs;
}): string {
  // Defaults for comparison
  const defaults = {
    topology: { raidLevel: 'raidz2' as const, drivesPerVdev: 6, vdevCount: 1 },
    drive: { driveSizeTB: 18, driveCost: 280, driveType: 'enterprise' as const },
    zfs: { ashift: 12, compressionRatio: 1.0 },
    utilization: { poolFillPercent: 80, freeSpaceLimitPercent: 20, scrubIntervalDays: 30 },
    economics: { electricityRate: 0.12, uptimeHoursPerDay: 24 },
    performance: { workloadType: 'mixed' as const, readPercentage: 0.50 },
  };

  // Create minimal config (only non-default values)
  const minimal: MinimalConfig = {};

  // Topology
  if (config.topology.raidLevel !== defaults.topology.raidLevel ||
      config.topology.drivesPerVdev !== defaults.topology.drivesPerVdev ||
      config.topology.vdevCount !== defaults.topology.vdevCount) {
    minimal.t = {
      ...(config.topology.raidLevel !== defaults.topology.raidLevel && { r: config.topology.raidLevel }),
      ...(config.topology.drivesPerVdev !== defaults.topology.drivesPerVdev && { d: config.topology.drivesPerVdev }),
      ...(config.topology.vdevCount !== defaults.topology.vdevCount && { v: config.topology.vdevCount }),
    };
  }

  // Drive
  if (config.drive.driveSizeTB !== defaults.drive.driveSizeTB ||
      config.drive.driveCost !== defaults.drive.driveCost ||
      config.drive.driveType !== defaults.drive.driveType) {
    minimal.dr = {
      ...(config.drive.driveSizeTB !== defaults.drive.driveSizeTB && { s: config.drive.driveSizeTB }),
      ...(config.drive.driveCost !== defaults.drive.driveCost && { c: config.drive.driveCost }),
      ...(config.drive.driveType !== defaults.drive.driveType && { t: config.drive.driveType }),
    };
  }

  // ZFS
  if (config.zfs.ashift !== defaults.zfs.ashift ||
      config.zfs.compressionRatio !== defaults.zfs.compressionRatio) {
    minimal.z = {
      ...(config.zfs.ashift !== defaults.zfs.ashift && { a: config.zfs.ashift }),
      ...(config.zfs.compressionRatio !== defaults.zfs.compressionRatio && { cr: config.zfs.compressionRatio }),
    };
  }

  // Utilization
  if (config.utilization.poolFillPercent !== defaults.utilization.poolFillPercent ||
      config.utilization.freeSpaceLimitPercent !== defaults.utilization.freeSpaceLimitPercent ||
      config.utilization.scrubIntervalDays !== defaults.utilization.scrubIntervalDays) {
    minimal.u = {
      ...(config.utilization.poolFillPercent !== defaults.utilization.poolFillPercent && { f: config.utilization.poolFillPercent }),
      ...(config.utilization.freeSpaceLimitPercent !== defaults.utilization.freeSpaceLimitPercent && { l: config.utilization.freeSpaceLimitPercent }),
      ...(config.utilization.scrubIntervalDays !== defaults.utilization.scrubIntervalDays && { sd: config.utilization.scrubIntervalDays }),
    };
  }

  // Economics
  if (config.economics.electricityRate !== defaults.economics.electricityRate ||
      config.economics.uptimeHoursPerDay !== defaults.economics.uptimeHoursPerDay) {
    minimal.e = {
      ...(config.economics.electricityRate !== defaults.economics.electricityRate && { r: config.economics.electricityRate }),
      ...(config.economics.uptimeHoursPerDay !== defaults.economics.uptimeHoursPerDay && { h: config.economics.uptimeHoursPerDay }),
    };
  }

  // Performance
  if (config.performance.workloadType !== defaults.performance.workloadType ||
      config.performance.readPercentage !== defaults.performance.readPercentage) {
    minimal.p = {
      ...(config.performance.workloadType !== defaults.performance.workloadType && { w: config.performance.workloadType }),
      ...(config.performance.readPercentage !== defaults.performance.readPercentage && { rp: config.performance.readPercentage }),
    };
  }

  // Convert to JSON and encode
  const json = JSON.stringify(minimal);
  const compressed = btoa(json);

  // Create URL
  const url = new URL(window.location.href);
  url.searchParams.set('c', compressed);

  return url.toString();
}

/**
 * Parse configuration from URL parameter
 *
 * @param url - URL string containing encoded configuration
 * @returns Parsed configuration or null if no valid config found
 */
export function configFromURL(url: string): {
  topology: StorageTopology;
  drive: DriveParameters;
  zfs: ZFSParameters;
  utilization: CapacityUtilization;
  economics: EconomicsParameters;
  performance: PerformanceInputs;
} | null {
  try {
    const params = new URL(url).searchParams;
    const compressed = params.get('c');

    if (!compressed) return null;

    const json = atob(compressed);
    const minimal: MinimalConfig = JSON.parse(json);

    // Expand minimal config to full config with defaults
    return expandConfig(minimal);
  } catch (e) {
    console.error('Failed to parse config from URL:', e);
    return null;
  }
}

/**
 * Expand minimal config to full configuration with defaults
 */
function expandConfig(minimal: MinimalConfig): {
  topology: StorageTopology;
  drive: DriveParameters;
  zfs: ZFSParameters;
  utilization: CapacityUtilization;
  economics: EconomicsParameters;
  performance: PerformanceInputs;
} {
  return {
    topology: {
      raidLevel: minimal.t?.r ?? 'raidz2',
      drivesPerVdev: minimal.t?.d ?? 6,
      vdevCount: minimal.t?.v ?? 1,
    },
    drive: {
      driveSizeTB: minimal.dr?.s ?? 18,
      driveCost: minimal.dr?.c ?? 280,
      wattsIdle: 6.5,
      wattsLoad: 9.5,
      driveIOPS: 90,
      afr: 0.95,
      peakSequentialMBps: 255,
      driveType: minimal.dr?.t ?? 'enterprise',
    },
    zfs: {
      ashift: (minimal.z?.a ?? 12) as 9 | 10 | 11 | 12 | 13,
      slopPercent: 3.125,
      recordsize: 128,
      compressionRatio: minimal.z?.cr ?? 1.0,
    },
    utilization: {
      poolFillPercent: minimal.u?.f ?? 80,
      freeSpaceLimitPercent: minimal.u?.l ?? 20,
      scrubIntervalDays: minimal.u?.sd ?? 30,
    },
    economics: {
      electricityRate: minimal.e?.r ?? 0.12,
      uptimeHoursPerDay: minimal.e?.h ?? 24,
    },
    performance: {
      workloadType: minimal.p?.w ?? 'mixed',
      readPercentage: minimal.p?.rp ?? 0.50,
    },
  };
}
